import { useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import useConfigStore, { CONFIG_TABS, DEFAULT_SETTINGS } from '@/store/useConfigStore';
import useAuthStore from '@/store/useAuthStore';
import { saveConfigApi } from '@/api/configApi';

// Debounce delay for auto-save (ms)
const AUTO_SAVE_DELAY = 500;

/**
 * Hook for managing the ConfigModal with URL sync and auto-save.
 *
 * URL patterns:
 * - ?config        → opens modal on default tab (audio)
 * - ?config=audio  → opens modal on audio tab
 * - ?config=display → opens modal on display tab
 * - ?config=gameplay → opens modal on gameplay tab
 *
 * Auto-save: Settings are saved automatically after changes (debounced).
 */
export default function useConfigModal() {
  const location = useLocation();
  const navigate = useNavigate();

  // Ref for debounce timer
  const saveTimeoutRef = useRef(null);
  // Ref to track if initial load is done (to avoid saving on mount)
  const isInitializedRef = useRef(false);

  // Store state and actions
  const isOpen = useConfigStore((state) => state.isOpen);
  const activeTab = useConfigStore((state) => state.activeTab);
  const settings = useConfigStore((state) => state.settings);
  const openModal = useConfigStore((state) => state.openModal);
  const closeModal = useConfigStore((state) => state.closeModal);
  const setTab = useConfigStore((state) => state.setTab);
  // Auth store (user needed for change detection)
  const user = useAuthStore((state) => state.user);

  // Mutation for saving settings (silent - no feedback messages)
  const saveMutation = useMutation({
    mutationFn: saveConfigApi, // saveConfigApi already updates useAuthStore
    // Silent - no feedback.success() for auto-save
    // Errors handled centrally by api.js
  });

  // Mark modal as initialized when it opens (for auto-save tracking)
  // NOTE: We NO LONGER reload from backend here - useConfigStore is the SSOT
  // Settings are loaded once on app init (from IndexedDB) and after login (from backend)
  useEffect(() => {
    if (isOpen) {
      isInitializedRef.current = true;
    } else {
      isInitializedRef.current = false;
    }
  }, [isOpen]);

  // Auto-save when settings change (debounced)
  const debouncedSave = useCallback((newSettings) => {
    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Schedule new save
    saveTimeoutRef.current = setTimeout(() => {
      saveMutation.mutate(newSettings);
    }, AUTO_SAVE_DELAY);
  }, [saveMutation]);

  // Watch for settings changes and auto-save
  useEffect(() => {
    // Don't save on initial load or if modal is closed
    if (!isInitializedRef.current || !isOpen) return;

    // Compare with current user config to avoid unnecessary saves
    const currentConfig = user?.properties?.config || DEFAULT_SETTINGS;
    const hasChanges = JSON.stringify(settings) !== JSON.stringify({ ...DEFAULT_SETTINGS, ...currentConfig });

    if (hasChanges) {
      debouncedSave(settings);
    }

    // Cleanup timeout on unmount
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [settings, isOpen, user?.properties?.config, debouncedSave]);

  // Detect URL query param on mount/location change
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const configParam = searchParams.get('config');

    if (configParam !== null) {
      // ?config exists (could be empty string or a tab name)
      const tab = CONFIG_TABS.includes(configParam) ? configParam : 'audio';
      openModal(tab);
    }
  }, [location.search, openModal]);

  // Update URL when tab changes (only if modal is open)
  useEffect(() => {
    if (!isOpen) return;

    const searchParams = new URLSearchParams(location.search);
    const currentConfigParam = searchParams.get('config');

    // Only update if different from current
    if (currentConfigParam !== activeTab) {
      searchParams.set('config', activeTab);
      navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
    }
  }, [activeTab, isOpen, location.pathname, location.search, navigate]);

  // Handle modal close - clean URL
  const handleClose = () => {
    // Flush any pending save immediately
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      // Save immediately if there are pending changes
      const currentConfig = user?.properties?.config || DEFAULT_SETTINGS;
      const hasChanges = JSON.stringify(settings) !== JSON.stringify({ ...DEFAULT_SETTINGS, ...currentConfig });
      if (hasChanges) {
        saveMutation.mutate(settings);
      }
    }

    closeModal();

    // Remove config param from URL
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('config');

    const newSearch = searchParams.toString();
    const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
    navigate(newUrl, { replace: true });
  };

  // Open modal with optional tab (updates URL)
  const handleOpen = (tab = 'audio') => {
    const validTab = CONFIG_TABS.includes(tab) ? tab : 'audio';
    openModal(validTab);

    // Update URL
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('config', validTab);
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  };

  return {
    // State
    isOpen,
    activeTab,
    settings,
    isSaving: saveMutation.isPending,

    // Actions
    openModal: handleOpen,
    closeModal: handleClose,
    setTab,
  };
}
